import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const logoIcon = "https://customer-assets.emergentagent.com/job_nina-beauty-academy/artifacts/79ucravx_WhatsApp_Image_2026-03-09_at_11.56.55_PM__1_-removebg-preview.png";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/courses', label: t.nav.courses },
    { path: '/schedule', label: t.nav.schedule },
    { path: '/services', label: t.nav.services },
    { path: '/gallery', label: t.nav.gallery },
    { path: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-b border-[#E6E2D6]/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-300"
          >
            <img 
              src={logoIcon} 
              alt="NC" 
              className="h-8 w-auto object-contain"
            />
            <span className="font-playfair text-2xl font-medium text-[#1A1A1A]">
              Nina Beauty
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-manrope text-sm uppercase tracking-[0.15em] transition-colors duration-300 relative group ${
                  isActive(link.path)
                    ? 'text-[#C5A059]'
                    : 'text-[#666660] hover:text-[#C5A059]'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-[1px] bg-[#C5A059] transition-all duration-300 ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6E2D6] hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-all duration-300"
            >
              <Globe className="w-4 h-4 text-[#C5A059]" />
              <span className="font-manrope text-sm font-medium text-[#1A1A1A]">
                {language === 'en' ? 'AR' : 'EN'}
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-[#F5F5F0] transition-colors duration-300"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A1A1A]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A1A1A]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-6 space-y-4 border-t border-[#E6E2D6]">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                className={`block py-2 font-manrope text-base transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-[#C5A059]'
                    : 'text-[#666660] hover:text-[#C5A059]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};
